/**
 * Module: `search/fuzzy.ts` — client library utilities.
 * @remarks See exports; keep imports from concrete files when possible.
 */

import Fuse, { type Expression, type FuseResult } from "fuse.js";

import { searchQueryTerms } from "@/lib/search/query-terms";
import type {
  ConversationMetadata,
  ConversationSearchResult,
  MatchIndices,
} from "@/types";

/**
 * Flattened structure for Fuse.js indexing
 * Each question becomes a separate searchable entry
 */
interface SearchableEntry {
  id: string | number;
  conversationId: string | number;
  type: "title" | "question";
  content: string;
  questionId?: string;
}

/**
 * Default Fuse.js options for conversation search
 * - Multi-word: logical $and (chaque terme doit matcher, ordre libre)
 * - Fuzzy très léger: léger laxisme sur fautes de frappe (threshold bas)
 */
/** Options partagées recherche liste + surlignage (1:1). */
const FUSE_BASE_OPTIONS = {
  isCaseSensitive: false,
  ignoreDiacritics: true,
  useExtendedSearch: false,
  ignoreLocation: true,
  /** ~0.18 : tolère une typo légère sans trop de bruit (0 = strict) */
  threshold: 0.18,
  minMatchCharLength: 2,
} as const;

const DEFAULT_FUSE_OPTIONS = {
  ...FUSE_BASE_OPTIONS,
  includeMatches: false,
  includeScore: true,
};

/**
 * Create a Fuse.js instance for searching conversations
 */
export function createConversationSearcher(
  conversations: ConversationMetadata[],
  questionsMap: Map<string, ConversationMetadata["questions"]>
): Fuse<SearchableEntry> {
  const searchableEntries: SearchableEntry[] = [];

  conversations.forEach((conv) => {
    // Add title as searchable entry
    searchableEntries.push({
      id: `${conv.id}-title`,
      conversationId: conv.id,
      type: "title",
      content: conv.title,
    });

    // Add questions as searchable entries
    const questions = questionsMap.get(String(conv.id)) || [];
    questions.forEach((question) => {
      searchableEntries.push({
        id: `${conv.id}-q-${question.id}`,
        conversationId: conv.id,
        type: "question",
        content: question.content,
        questionId: question.id,
      });
    });
  });

  return new Fuse(searchableEntries, {
    ...DEFAULT_FUSE_OPTIONS,
    keys: [{ name: "content", weight: 1 }],
  });
}

const CONTENT_PATH = ["content"] as const;

/**
 * Une requête Fuse : un mot → chaîne fuzzy ; plusieurs mots → $and sur le champ content.
 */
export function toSearchExpression(query: string): Expression | null {
  const terms = searchQueryTerms(query);
  if (terms.length === 0) {
    return null;
  }
  if (terms.length === 1) {
    return terms[0];
  }
  return {
    $and: terms.map((term) => ({
      $path: [...CONTENT_PATH],
      $val: term,
    })),
  };
}

function fuseMatchIndices(text: string, expression: Expression): MatchIndices {
  const fuse = new Fuse([{ content: text }], {
    ...FUSE_BASE_OPTIONS,
    includeMatches: true,
    keys: [{ name: "content", weight: 1 }],
  });

  const results = fuse.search(expression);
  const matches = results[0]?.matches;
  if (!matches?.length) {
    return [];
  }

  const indices: [number, number][] = [];
  for (const match of matches) {
    for (const range of match.indices) {
      indices.push([range[0], range[1]]);
    }
  }
  return indices;
}

/**
 * Indices [start, end] inclusifs pour surligner `text` — même Fuse / requête que la liste.
 *
 * Multi-mots : AND Fuse sur le texte entier ; si aucun match (segment partiel, ex. pastille
 * gatekeeper), repli terme par terme pour surligner chaque token présent dans ce segment.
 */
export function computeFuseHighlightIndices(
  text: string,
  searchQuery: string
): MatchIndices {
  if (!text || !searchQuery.trim()) {
    return [];
  }

  const terms = searchQueryTerms(searchQuery);
  if (terms.length === 0) {
    return [];
  }

  const expression = toSearchExpression(searchQuery);
  if (expression === null) {
    return [];
  }

  const indices = fuseMatchIndices(text, expression);
  if (indices.length > 0 || terms.length <= 1) {
    return indices;
  }

  const perTerm: MatchIndices = [];
  for (const term of terms) {
    perTerm.push(...fuseMatchIndices(text, term));
  }
  return perTerm;
}

/**
 * Search conversations with fuzzy matching and diacritics support
 * Returns results grouped by conversation with match information
 */
export function searchConversationsWithFuzzy(
  fuse: Fuse<SearchableEntry>,
  query: string,
  conversations: ConversationMetadata[]
): ConversationSearchResult[] {
  if (!query || !query.trim()) {
    // Return all conversations with no matches when no query
    return conversations.map((conv) => ({
      conversation: conv,
      score: 0,
      questionMatches: new Map(),
    }));
  }

  const expression = toSearchExpression(query);
  if (expression === null) {
    return [];
  }

  const results = fuse.search(expression);

  // Group results by conversation
  const conversationResultsMap = new Map<
    string | number,
    ConversationSearchResult
  >();

  results.forEach((result: FuseResult<SearchableEntry>) => {
    const { item, score } = result;
    const convId = item.conversationId;

    // Find the conversation
    const conversation = conversations.find((c) => c.id === convId);
    if (!conversation) return;

    // Get or create result entry
    let convResult = conversationResultsMap.get(convId);
    if (!convResult) {
      convResult = {
        conversation,
        score: score ?? 1,
        questionMatches: new Map(),
      };
      conversationResultsMap.set(convId, convResult);
    }

    // Update best score (lower is better in Fuse.js)
    if (score !== undefined && score < convResult.score) {
      convResult.score = score;
    }

    if (item.type === "question" && item.questionId) {
      convResult.questionMatches.set(item.questionId, []);
    }
  });

  // Convert map to array and sort by score (lower is better)
  return Array.from(conversationResultsMap.values()).sort(
    (a, b) => a.score - b.score
  );
}

/**
 * Check if a conversation has any question matches
 */
export function hasQuestionMatches(result: ConversationSearchResult): boolean {
  return result.questionMatches.size > 0;
}
